import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ListChecks, Plus, Trash2, Save } from "lucide-react";

const CAT_LABEL = { housekeeping: "Καθαριότητα", maintenance: "Συντήρηση", gardening: "Κηπουρική" };
const CLEAN = [{ k: "freshen_up", l: "Freshen up" }, { k: "deep_cleaning", l: "Βαθύς" }, { k: "checkout", l: "Checkout" }];

export default function FieldOpsChecklists({ embedded }) {
  const qc = useQueryClient();
  const [cat, setCat] = useState("housekeeping");
  const [items, setItems] = useState([]);
  const { data: lists = [] } = useQuery({ queryKey: ["fo-checklists"], queryFn: async () => (await api.get("/fieldops/checklists")).data });

  useEffect(() => {
    const cl = lists.find((l) => l.category === cat);
    setItems(cl ? cl.items.map((i) => ({ ...i })) : []);
  }, [cat, lists]);

  const save = useMutation({
    mutationFn: async () => (await api.post("/fieldops/checklists", { category: cat, name: CAT_LABEL[cat], items })).data,
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); qc.invalidateQueries({ queryKey: ["fo-checklists"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const upd = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const toggleReq = (i, ct) => {
    const it = items[i];
    const has = (it.required_for || []).includes(ct);
    upd(i, { required_for: has ? it.required_for.filter((x) => x !== ct) : [...(it.required_for || []), ct] });
  };

  return (
    <div className="space-y-6" data-testid="fo-checklists-page">
      <div>
        {!embedded && <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Εργασίες Πεδίου</p>}
        <h1 className={`font-serif ${embedded ? "text-2xl" : "text-3xl md:text-4xl mt-1"} text-[#45423C] inline-flex items-center gap-2`}><ListChecks className="h-6 w-6" /> Checklists</h1>
      </div>
      <div className="flex gap-2">
        {Object.keys(CAT_LABEL).map((c) => (
          <button key={c} data-testid={`cl-cat-${c}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-xl text-sm font-semibold border ${cat === c ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] text-[#6F6A60] bg-white"}`}>{CAT_LABEL[c]}</button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-2">
        {cat === "housekeeping" && <p className="text-xs text-[#8A8577] mb-2">Επίλεξε για ποιους τύπους καθαρισμού απαιτείται κάθε σημείο (κανένα = πάντα).</p>}
        {items.map((it, i) => (
          <div key={i} data-testid={`cl-item-${i}`} className="rounded-xl border border-[#E3DED4] px-3 py-2 flex items-center gap-2 flex-wrap">
            <input value={it.label} onChange={(e) => upd(i, { label: e.target.value })} className="flex-1 min-w-[160px] rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm" />
            {cat === "housekeeping" && CLEAN.map((c) => (
              <button key={c.k} onClick={() => toggleReq(i, c.k)} className={`text-[11px] px-2 py-1 rounded-full border ${(it.required_for || []).includes(c.k) ? "bg-[#6E7A63] text-white border-[#6E7A63]" : "border-[#E3DED4] text-[#8A8577]"}`}>{c.l}</button>
            ))}
            <button data-testid={`cl-del-${i}`} onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="h-8 w-8 rounded-lg border border-[#E7CEC6] flex items-center justify-center text-[#A1493A]"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button data-testid="cl-add-item" onClick={() => setItems([...items, { id: "", label: "Νέο σημείο", required_for: [] }])} className="mt-2 rounded-xl border border-dashed border-[#D8D0C2] px-4 py-2 text-sm text-[#6F6A60] inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> Προσθήκη σημείου</button>
      </div>
      <button data-testid="cl-save" onClick={() => save.mutate()} className="rounded-xl bg-[#45423C] text-white px-5 py-3 text-sm font-semibold inline-flex items-center gap-2"><Save className="h-4 w-4" /> Αποθήκευση checklist</button>
    </div>
  );
}
